import { SvgConsumer } from '@components/icons/svgConsumer';
import { cn } from '@utils/cn';
import { use24h } from '@utils/consts';
import { parseDate } from '@utils/dateHelpers';
import {
  FocusEvent,
  FormEvent,
  HTMLAttributes,
  KeyboardEvent,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { useTranslation } from 'react-i18next';
import { InputMask } from '@react-input/mask';
import { Clock } from 'lucide-react';
import { HoverCard, HoverCardContent, HoverCardTrigger } from '@components/ui/hoverCard';
import { Button } from '@components/ui/button';
import { useOutsideClick } from '@hooks/useOutsideClick';
import { validateKeyDown } from '@utils/formHelpers';

interface TimeInputProps extends HTMLAttributes<HTMLInputElement> {
  asInput?: boolean;
  disabled?: boolean;
  isEmpty?: boolean;
  value?: string;
  onValueChange?: (x: string) => void;
  onInputChange?: (date: Date) => void;
  onFocusLost?: (x: string) => void;
}

export const TimeInput = (props: TimeInputProps) => {
  const { t } = useTranslation('app');
  const {
    id,
    defaultValue,
    disabled,
    isEmpty,
    onBlur,
    onFocusLost,
    onValueChange,
    onInputChange,
    onChange,
    asInput,
  } = props;
  const [err, setErr] = useState<string>('');
  const [val, setVal] = useState<string>('');
  const validate12hFormat = /^(1[0-2]|0?[1-9]):[0-5][0-9] (AM|PM)$/;
  const validate24hFormat = /^([01][0-9]|2[0-3]):([0-5][0-9])$/;
  const [focused, setFocused] = useState<boolean>();
  const [h, setH] = useState<string>('');
  const [m, setM] = useState<string>('');
  const [a, setA] = useState<string>('');
  const hoverRef = useRef<HTMLDivElement | null>(null);
  useOutsideClick(hoverRef, () => setFocused(typeof focused !== 'boolean' ? undefined : false));

  const hours = useMemo(() => {
    return new Array(use24h ? 24 : 12).fill('').map((_e, i) => (i < 9 ? `0${i + 1}` : `${i + 1}`));
  }, []);
  const minutes = useMemo(() => {
    return new Array(60).fill('').map((_e, i) => (i < 9 ? `0${i}` : `${i}`));
  }, []);

  function handleChange(ev: FormEvent<HTMLInputElement>) {
    setErr('');
    const { value: v } = ev.target as HTMLInputElement;
    const tmpV = v.toUpperCase();
    setVal(tmpV);
    setH(tmpV.slice(0, 2));
    setM(tmpV.slice(3, 5));
    setA(tmpV.slice(6, 8));
    if (onChange) onChange(ev);
    if (onValueChange) onValueChange(tmpV);
  }

  const getWidth = () => {
    if (use24h) {
      if (asInput) return 'w-10';
      return 'w-8';
    }
    if (asInput) return 'w-16';
    return 'w-14';
  };

  useEffect(() => {
    setErr('');
    if (typeof defaultValue === 'string') setVal(defaultValue);
  }, [defaultValue]);

  useEffect(() => {
    if (!val) {
      setH('');
      setM('');
      setA('');
      return;
    }
    const isValid = use24h ? validate24hFormat.test(val) : validate12hFormat.test(val);
    setErr(isValid ? '' : t(`errors.${use24h ? 'invalid_time_24' : 'invalid_time_12'}`));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [val]);

  useEffect(() => {
    if (!focused) return;
    const timer = setTimeout(() => {
      const selectedH = document.getElementById(`hour-${h}`);
      const selectedM = document.getElementById(`minute-${m}`);
      if (selectedH) selectedH.scrollIntoView();
      if (selectedM) selectedM.scrollIntoView();
    }, 100);
    return () => {
      clearTimeout(timer);
    };
  }, [focused, h, m]);

  useEffect(() => {
    if (typeof focused !== 'boolean' || focused) return;
    const isValid = use24h ? validate24hFormat.test(val) : validate12hFormat.test(val);
    if (!isValid) return;
    const parsedV = use24h ? parseDate(val, 'HH:mm') : parseDate(val, 'hh:mm a');
    if (onFocusLost) onFocusLost(parsedV.toISOString());
    if (onInputChange) onInputChange(parsedV);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [focused]);

  return (
    <div className={cn('flex w-fit flex-col', disabled && 'cursor-not-allowed')} ref={hoverRef}>
      <HoverCard open={!disabled ? focused : false}>
        <HoverCardTrigger>
          <div
            className={cn(
              'flex w-fit items-center gap-1',
              asInput &&
                'group-[.error]:shadow-danger-b flex h-8 items-center gap-2 rounded border border-neutral-300 bg-white px-3 text-s text-neutral-800 focus-within:border-primary-600 focus-within:outline focus-within:outline-2 focus-within:outline-primary-200',
              err && 'border-danger-500 focus-within:border-danger-500',
              props.disabled && 'border-neutral-200 bg-neutral-50 text-neutral-400',
            )}>
            {isEmpty && (
              <label htmlFor={`${id}-time`}>
                <SvgConsumer id="warning" className="h-4 w-4 shrink-0" />
              </label>
            )}
            <InputMask
              mask={use24h ? '__:__' : '__:__ __'}
              replacement="_"
              id={`${id}-time`}
              type="text"
              disabled={disabled}
              onFocus={() => {
                setFocused(true);
              }}
              onBlur={(e: FocusEvent<HTMLInputElement>) => {
                if (onBlur) onBlur(e);
              }}
              className={cn(
                getWidth(),
                asInput &&
                  'flex bg-transparent py-1 text-m leading-none ring-offset-background placeholder:text-neutral-400 focus-visible:outline-none',
                'border-b-none bg-transparent disabled:cursor-not-allowed',
              )}
              value={val}
              placeholder={use24h ? '--:--' : '--:-- --'}
              onChange={handleChange}
              autoCapitalize="none"
              autoCorrect="off"
              autoComplete="off"
              onKeyDown={(e: KeyboardEvent<HTMLInputElement>) => {
                const { selectionStart } = e.target as HTMLInputElement;

                if (selectionStart! === 0 && !validateKeyDown(e, use24h ? /[0-2]/ : /[0-1]/))
                  e.preventDefault();
                if (
                  (selectionStart! === 1 || selectionStart! === 3) &&
                  !validateKeyDown(e, /[0-9]/)
                )
                  e.preventDefault();
                if (selectionStart! === 2 && !validateKeyDown(e, /[0-5]/)) e.preventDefault();
                if (selectionStart! === 5 && !validateKeyDown(e, /a|p|A|P/)) e.preventDefault();
                if (selectionStart! === 6 && !validateKeyDown(e, /m|M/)) e.preventDefault();
              }}
            />
            <button
              className="cursor-pointer disabled:cursor-not-allowed"
              type="button"
              disabled={disabled}
              onClick={() => setFocused(!focused)}>
              <Clock size={14} className="self-center" />
            </button>
          </div>
        </HoverCardTrigger>
        <HoverCardContent>
          <div className="flex h-24 gap-1">
            <div className="scrollbar-thin flex grow flex-col gap-1 overflow-y-auto">
              {hours.map((el) => (
                <button
                  id={`hour-${el}`}
                  key={`hour-${el}`}
                  onClick={(e) => {
                    e.stopPropagation();
                    e.preventDefault();
                    setH(el);
                    setVal((ps) => `${el}:${ps.slice(3)}`);
                  }}
                  className={cn(h === el && 'bg-primary-400 text-white')}>
                  {el}
                </button>
              ))}
            </div>
            <div className="scrollbar-thin flex grow flex-col gap-1 overflow-y-auto">
              {minutes.map((el) => (
                <button
                  id={`minute-${el}`}
                  key={`minute-${el}`}
                  onClick={(e) => {
                    e.stopPropagation();
                    e.preventDefault();
                    setM(el);
                    setVal((ps) => {
                      if (ps.length === 2) return `${ps}:${el}`;
                      if (!h) return `  :${el}${use24h ? '' : ' '}${ps.slice(6)}`;
                      return `${ps.slice(0, 2)}:${el}${use24h ? '' : ' '}${ps.slice(6)}`;
                    });
                  }}
                  className={cn(m === el && 'bg-primary-400 text-white')}>
                  {el}
                </button>
              ))}
            </div>
            {!use24h && (
              <div className="scrollbar-thin flex grow flex-col gap-1">
                <button
                  className={cn(a === 'AM' && 'bg-primary-400 text-white')}
                  onClick={(e) => {
                    e.stopPropagation();
                    e.preventDefault();
                    setA('AM');
                    setVal((ps) => `${ps.slice(0, 5)} AM`);
                  }}>
                  AM
                </button>
                <button
                  className={cn(a === 'PM' && 'bg-primary-400 text-white')}
                  onClick={(e) => {
                    e.stopPropagation();
                    e.preventDefault();
                    setA('PM');
                    setVal((ps) => `${ps.slice(0, 5)} PM`);
                  }}>
                  PM
                </button>
              </div>
            )}
          </div>
          <Button
            size="sm"
            className="mt-2 w-full"
            onClick={() => {
              setFocused(false);
            }}>
            {t('actions.save')}
          </Button>
        </HoverCardContent>
      </HoverCard>
      {err && <p className="mt-2 text-xs font-normal text-danger-600">{err}</p>}
    </div>
  );
};
